import classNames from 'classnames';
import {SelectionRange, type TextSelection} from 'prosemirror-state';
import {type MarkType, type Node} from 'prosemirror-model';
import {
    callOrReturn,
    InputRule,
    type Mark,
    markInputRule,
    mergeAttributes,
    type EditorEvents,
} from '@tiptap/core';
import {Link} from '@tiptap/extension-link';
import {Plugin, PluginKey} from '@tiptap/pm/state';
import type {LinkOptions as OriginalLinkOptions} from '@tiptap/extension-link';
import {find} from 'linkifyjs';
import {Decoration, DecorationSet} from 'prosemirror-view';
import {sendToStatist} from 'packages/statist';

import Constants from 'utils/constants';

import {clearFormatStoredMarks} from '../utils';
import appStyles from '../app-post-style.module.css';

import {hasProtocol} from '../../components/link-popovers/utils';
import {findAncestorAnchor} from '../../utils';

type LinkAttrs = {
    href: string;
    title?: string;
}

type PluginState = {
    shouldHighlight: boolean;
    highlightRange?: {
        from: number;
        to: number;
    };
}

export interface LinkOptions extends OriginalLinkOptions {
    excludedMarks: Mark[];
    handleLinkPreviewEvent: (
        event: 'mouseover' | 'mouseout' | 'escape' | 'disable' | 'enable' | 'caretin' | 'caretout',
        node?: HTMLAnchorElement | null,
        delay?: number,
    ) => void;
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        linkCommands: {
            addLinkToSelection: (attrs: LinkAttrs) => ReturnType;
            toggleLinkHighlight: (shouldHighlight: boolean, domNode?: HTMLElement | null) => ReturnType;
            updateLink: (domNode: HTMLElement, href: string) => ReturnType;
            deleteLink: (domNode: HTMLElement) => ReturnType;
        };
    }
}

const protocols = new Set(['ftp', 'mailto', 'timeapp', 'http', 'https', 'jamfselfservice']);

export const dataLinkPreSelectionAttribute = 'data-pre-link-selection';
export const dataLinkPopoverTarget = 'data-link-popover-target';
export const LINK_POPOVER_EVENT = 'LINK_POPOVER_EVENT' as keyof EditorEvents;

export const linkPluginKey = new PluginKey<PluginState>('linkPlugin');

function linkInputRule(config: Parameters<typeof markInputRule>[0]) {
    const defaultMarkInputRule = markInputRule(config);

    return new InputRule({
        find: config.find,
        handler(props) {
            const attributes = callOrReturn(config.getAttributes, undefined, props.match.slice());

            if (!attributes) {
                return null;
            }

            const protocol = attributes.href?.split(':')[0];

            if (protocol && !protocols.has(protocol.toLowerCase())) {
                return null;
            }

            defaultMarkInputRule.handler(props);
            clearFormatStoredMarks(props.state);
            props.state.tr.setMeta('preventAutolink', true);
        },
    });
}

/**
 * @see https://github.com/ueberdosis/tiptap/discussions/1865
 */
export const TimeWebkitLinkExtension = Link.extend<LinkOptions>({
    inclusive: false,
    renderHTML({HTMLAttributes}) {
        return [
            'a',
            mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
                class: classNames(HTMLAttributes.class, appStyles.markdown__link),
            }),
            0,
        ];
    },
    addInputRules() {
        return [
            linkInputRule({
                find: /(?:^|\s)\[([^\]]*)?]\((\S+)(?: ["“](.+)["”])?\)\s$/i,
                type: this.type,
                getAttributes(match) {
                    return {
                        title: match.pop()?.trim(),
                        href: match.pop()?.trim(),
                    };
                },
            }),
            linkInputRule({
                find: /(?:^|\s)<([a-zA-Z]+:\S+)>\s$/i,
                type: this.type,
                getAttributes(match) {
                    return {
                        href: match[match.length - 1]?.trim(),
                    };
                },
            }),
        ];
    },
    addCommands() {
        return {
            addLinkToSelection: (attrs) => ({state, dispatch}) => {
                const markType = this.type;
                const {empty, $cursor, ranges: initialRanges} = state.selection as TextSelection;
                const ranges = filterRanges(initialRanges, this.options.excludedMarks);
                const enterAtoms = false;
                if ($cursor) {
                    return false;
                }
                if (empty || !markApplies(state.doc, ranges, markType, enterAtoms)) {
                    return false;
                }
                if (dispatch) {
                    const tr = state.tr;
                    for (const next of ranges) {
                        const {$from, $to} = next;
                        dispatch(tr.addMark($from.pos, $to.pos, markType.create(attrs)));
                    }
                }
                return true;
            },
            toggleLinkHighlight: (shouldHighlight, domNode) => ({state, view, dispatch}) => {
                if (!domNode) {
                    dispatch?.(state.tr.setMeta(linkPluginKey, {shouldHighlight}));
                    return true;
                }

                const pos = view.posAtDOM(domNode, 0);
                const node = state.doc.nodeAt(pos);

                if (node && node.marks.some((mark) => mark.type.name === this.type.name)) {
                    const highlightRange = shouldHighlight ? {
                        from: pos,
                        to: pos + node.nodeSize,
                    } : undefined;
                    dispatch?.(state.tr.setMeta(linkPluginKey, {
                        shouldHighlight,
                        highlightRange,
                    }));
                    return true;
                }

                return false;
            },
            updateLink: (domNode: HTMLElement, href: string) => ({state, view, dispatch}) => {
                const pos = view.posAtDOM(domNode, 0);
                const node = state.doc.nodeAt(pos);

                if (node && node.marks.some((mark) => mark.type.name === this.type.name)) {
                    const transaction = state.tr.removeMark(pos, pos + node.nodeSize, this.type);
                    transaction.addMark(pos, pos + node.nodeSize, this.type.create({href}));
                    dispatch?.(transaction);
                    return true;
                }

                return false;
            },
            deleteLink: (domNode: HTMLElement) => ({state, view, dispatch}) => {
                const pos = view.posAtDOM(domNode, 0);
                const node = state.doc.nodeAt(pos);

                if (node && node.marks.some((mark) => mark.type.name === this.type.name)) {
                    dispatch?.(state.tr.removeMark(pos, pos + node.nodeSize, this.type));
                    return true;
                }

                return false;
            },
        };
    },
    addProseMirrorPlugins() {
        return [
            ...this.parent?.() || [],
            new Plugin<PluginState>({
                key: linkPluginKey,
                state: {
                    init: () => ({shouldHighlight: false, highlightRange: undefined}),
                    apply(tr, prev) {
                        const meta = tr.getMeta(linkPluginKey);
                        return meta ? {...prev, ...meta} : prev;
                    },
                },
                props: {
                    handleKeyDown: (view, event) => {
                        if (event.key === Constants.KeyCodes.ESCAPE[0]) {
                            return this.options.handleLinkPreviewEvent('escape');
                        }
                    },
                    handlePaste: (view, _event, slice) => {
                        let textContent = '';

                        slice.content.forEach((node) => {
                            textContent += node.textContent;
                        });

                        const link = find(textContent, {
                            defaultProtocol: this.options.defaultProtocol,
                        }).find(
                            (item) => item.isLink && item.value === textContent,
                        );

                        if (!textContent || !link) {
                            return false;
                        }

                        sendToStatist('editor.link.apply', {
                            highlight: !view.state.selection.empty,
                            insert: true,
                            text: true,
                            protocol: hasProtocol(textContent.trim()),
                        });

                        return this.editor.commands.addLinkToSelection({
                            href: link.href,
                        });
                    },
                    handleDOMEvents: {
                        mouseover: (_, event: MouseEvent) => {
                            const anchorEl = findAncestorAnchor(event.target);
                            if (anchorEl) {
                                this.options.handleLinkPreviewEvent('mouseover', anchorEl, 500);
                            }
                        },
                        mouseout: (_, event) => {
                            const anchorEl = findAncestorAnchor(event.target);
                            if (anchorEl) {
                                this.options.handleLinkPreviewEvent('mouseout', null, 300);
                            }
                        },
                    },
                    decorations: (state) => {
                        const pluginState = linkPluginKey.getState(state);

                        if (!pluginState) {
                            return;
                        }

                        const {selection, doc} = state;

                        const hasSelection = Boolean(pluginState.highlightRange) || !selection.empty;

                        if (!pluginState.shouldHighlight || !hasSelection) {
                            return DecorationSet.empty;
                        }

                        const from = pluginState.highlightRange?.from ?? selection.from;
                        const to = pluginState.highlightRange?.to ?? selection.to;

                        const firstCharAttrs: Record<string, string> = {[dataLinkPopoverTarget]: ''};
                        const fullRangeAttrs: Record<string, any> = {
                            [dataLinkPreSelectionAttribute]: '',
                            style: 'color: rgb(var(--time-webkit-color-link-color-rgb)); text-decoration: underline;',
                        };

                        // inline-декоратор на весь диапазон
                        const fullRangeDeco = Decoration.inline(from, to, fullRangeAttrs);

                        // inline-декоратор только на первый символ
                        const firstCharDeco = Decoration.inline(from, from + 1, firstCharAttrs);

                        return DecorationSet.create(doc, [fullRangeDeco, firstCharDeco]);
                    },
                },
                view: () => {
                    const handleSelectionUpdate = (props: EditorEvents['selectionUpdate']) => {
                        const {state, view} = props.editor;

                        if (!state.selection.empty) {
                            this.options.handleLinkPreviewEvent('disable');
                            return;
                        }

                        this.options.handleLinkPreviewEvent('enable');

                        const resolvedPos = state.doc.resolve(state.selection.anchor);
                        const mark = resolvedPos.marks().find((mark) => {
                            return mark.type.name === TimeWebkitLinkExtension.name;
                        });
                        if (mark) {
                            const node = view.domAtPos(resolvedPos.pos).node.parentElement;
                            const anchorEl = findAncestorAnchor(node);
                            this.options.handleLinkPreviewEvent('caretin', anchorEl);
                        } else {
                            this.options.handleLinkPreviewEvent('caretout');
                        }
                    };

                    this.editor.on('selectionUpdate', handleSelectionUpdate);

                    return {
                        destroy: () => {
                            this.editor.off('selectionUpdate', handleSelectionUpdate);
                        },
                    };
                },
            }),
        ];
    },
}).configure({
    openOnClick: false,
    linkOnPaste: false, // выключаем плагин для вставки ссылки из оригинального экстеншена
    protocols: Array.from(protocols),
});

function markApplies(doc: Node, ranges: SelectionRange[], type: MarkType, enterAtoms: boolean) {
    for (const next of ranges) {
        const {$from, $to} = next;
        let can = $from.depth === 0 ? doc.inlineContent && doc.type.allowsMarkType(type) : false;
        doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
            if (can || (!enterAtoms && node.isAtom && node.isInline && pos >= $from.pos && pos + node.nodeSize <= $to.pos)) {
                return false;
            }
            can = node.inlineContent && node.type.allowsMarkType(type);
        });
        if (can) {
            return true;
        }
    }
    return false;
}

function filterRanges(ranges: readonly SelectionRange[], excludedMarks: LinkOptions['excludedMarks']): SelectionRange[] {
    const result = [];
    for (const next of ranges) {
        const {$from: $initialFrom, $to} = next;
        let $from = $initialFrom;

        const hasMark = (mark: Mark, node: Node) => {
            return node.marks.map((mark) => mark.type.name).includes(mark.name);
        };

        const getNodeText = (node: Node, nodeStart: number) => {
            const start = Math.max(0, $from.pos - nodeStart);
            const end = Math.min(node.nodeSize, $to.pos - nodeStart);
            return node.textBetween(start, end);
        };

        $from.doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
            let skip = false;

            if (node.isText && node.text && (/^\s*$/).test(getNodeText(node, pos))) {
                const nodeBefore = $from.doc.resolve(pos).nodeBefore;
                const nodeAfter = $from.doc.resolve(Math.min(pos + node.nodeSize, $to.pos)).nodeAfter;

                const isLinkBefore = nodeBefore?.marks.some((m) => m.type.name === TimeWebkitLinkExtension.name);
                const isLinkAfter = nodeAfter?.marks.some((m) => m.type.name === TimeWebkitLinkExtension.name);

                if (!isLinkBefore || !isLinkAfter) {
                    skip = true;
                }
            }
            if (excludedMarks.some((mark) => hasMark(mark, node))) {
                skip = true;
            }

            if (skip) {
                result.push(new SelectionRange($from, $from.doc.resolve(pos)));
                $from = $from.doc.resolve(Math.min(pos + node.nodeSize, $to.pos));
                return false;
            }
        });
        if ($from.pos < $to.pos) {
            result.push(new SelectionRange($from, $to));
        }
    }
    return result;
}
